import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiUrls } from "../useApiUrl";
import { apiUrls } from "../../utils/apiUrls";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import { http } from "../../utils/httpCommon";
import { toast } from "../../utils/snackbarHelper";
import { toastMessage } from "../../constants/strings";
import {
  RouteAssignmentResponse,
  TrapAssignmentResponse,
  routeAssignmentResponseSchema,
  trapAssignmentResponseSchema,
} from "../../types/planning/routeAssignmentResponse.type";
import { PlanTableFormat, PlanType } from "../../types/planning/plan.type";
import { EditedRoute } from "./useDeliveryRoutes.types";
import { plannedRoutesQueryKey } from "./usePlannedRoutes";
import { deliveryRoutesKeys } from "./useDeliveryRoutes";
import { shipmentsByRouteOrTrapKeys } from "./preplanning/useShipmentsByRouteOrTrap";

type RouteAssignmentProps = {
  date: number;
  sicId: number;
  searchTerm: string;
  onSuccess: (
    response?: RouteAssignmentResponse | TrapAssignmentResponse,
    request?: RouteAssignmentRequest
  ) => Promise<void> | void;
  onSettled?: () => void;
};

type RouteAssignmentRequest = {
  planId: number;
  route: EditedRoute;
};

type DeleteRouteAssignmentRequest = {
  planIds: number[];
};

export const useRouteAssignment = ({
  date,
  sicId,
  type,
  routeOrTrapId,
  onSuccess,
  onSettled,
}: RouteAssignmentProps & {
  type: PlanType;
  routeOrTrapId: number;
}) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const isEndpointHookEnabled = useIsFeatureFlagEnabled(
    "inbound-3763-endpoint-hook-client"
  );
  const { putRouteAssignment, putTrapAssignment } = isEndpointHookEnabled
    ? hookUrls
    : apiUrls;

  return useMutation({
    mutationFn: async ({ planId, route }: RouteAssignmentRequest) => {
      if (route.type === "delivery_route") {
        const { data } = await http.put<RouteAssignmentResponse>(
          putRouteAssignment(planId),
          route
        );
        return routeAssignmentResponseSchema.parse(data);
      } else {
        const { data } = await http.put<TrapAssignmentResponse>(
          putTrapAssignment(planId),
          route
        );
        return trapAssignmentResponseSchema.parse(data);
      }
    },
    onSuccess: async (
      response: RouteAssignmentResponse | TrapAssignmentResponse,
      variables
    ) => {
      await queryClient.invalidateQueries({
        queryKey: plannedRoutesQueryKey(date, sicId),
      });
      await onSuccess(response, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: shipmentsByRouteOrTrapKeys.shipmentsByRouteOrTrap(
          routeOrTrapId,
          type
        ),
      });
      onSettled?.();
    },
    onError: () => toast(toastMessage.generics.error, { variant: "error" }),
  });
};

export const useDeleteRouteAssignment = ({
  date,
  sicId,
  onSuccess,
  onSettled,
}: RouteAssignmentProps) => {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const isEndpointHookEnabled = useIsFeatureFlagEnabled(
    "inbound-3763-endpoint-hook-client"
  );
  const { deleteRouteAssignments } = isEndpointHookEnabled ? hookUrls : apiUrls;
  return useMutation({
    mutationFn: async ({ planIds }: DeleteRouteAssignmentRequest) => {
      const { status } = await http.delete(
        deleteRouteAssignments(planIds.join(","))
      );
      return status;
    },
    onSuccess: async (_, { planIds }) => {
      queryClient.setQueryData<PlanTableFormat[]>(
        plannedRoutesQueryKey(date, sicId),
        (previousStateOfPlans) => {
          if (!previousStateOfPlans) return [];
          return previousStateOfPlans.filter(
            (previousPlan) => !planIds.includes(previousPlan.id)
          );
        }
      );
      await queryClient.invalidateQueries({
        queryKey: deliveryRoutesKeys.deliveryRoutesSummary(sicId, date),
      });
      await onSuccess();
    },
    onError: () => toast(toastMessage.generics.error, { variant: "error" }),
    onSettled: () => {
      onSettled?.();
    },
  });
};
